import React, { useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Table, Tag, Button, Space, Typography, Spin } from 'antd';
import { ArrowLeft, Printer } from 'lucide-react';
import GlobalLayout from 'components/globalLayout';
import { useLanguage } from 'hooks/Languages/useLanguage';
import { useDeliveryNoteDetails } from 'queries/procurement/deliveryNotes';
import urls from 'routes/urls';
import { useFetchUserData } from 'queries/auth/user';
import { printDocument } from 'utils/printing/documentPrinter';

const { Title } = Typography;

const DeliveryNoteDetails = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { translate } = useLanguage();
    const componentRef = useRef<HTMLDivElement>(null);

    // Fetch Data
    const { dnDetailsData, loadingDNDetails } = useDeliveryNoteDetails(Number(id));
    const deliveryNote = dnDetailsData?.data;

    // Business Type Logic
    const { userData } = useFetchUserData();
    const businessType = userData?.business_type?.toLowerCase();
    const isPharmacy = businessType === 'pharmacy';

    const handlePrint = async () => {
        if (componentRef.current) {
            await printDocument(componentRef.current, {
                documentTitle: `Delivery_Note_${deliveryNote?.delivery_number || id}`
            });
        }
    };

    const columns = [
        {
            title: 'Product',
            dataIndex: 'product_name',
            key: 'product_name',
        },
        {
            title: 'SKU',
            dataIndex: 'sku',
            key: 'sku',
        },
        {
            title: 'Quantity Delivered',
            dataIndex: 'quantity_delivered',
            key: 'quantity_delivered',
        },
        {
            title: 'Accepted',
            dataIndex: 'quantity_accepted',
            key: 'quantity_accepted',
            render: (val: number) => <Tag color="green">{val}</Tag>
        },
        {
            title: 'Rejected',
            dataIndex: 'quantity_rejected',
            key: 'quantity_rejected',
            render: (val: number) => val > 0 ? <Tag color="red">{val}</Tag> : val
        },
        {
            title: 'Condition',
            dataIndex: 'condition',
            key: 'condition',
            render: (val: string) => <Tag color={val === 'good' ? 'blue' : 'orange'}>{val?.toUpperCase()}</Tag>
        },
        ...(isPharmacy ? [
            {
                title: 'Batch #',
                dataIndex: 'batch_number',
                key: 'batch_number',
            },
            {
                title: 'Expiry Date',
                dataIndex: 'expiry_date',
                key: 'expiry_date',
            }
        ] : [])
    ];

    if (loadingDNDetails) {
        return (
            <GlobalLayout>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <Spin size="large" tip="Loading details..." />
                </div>
            </GlobalLayout>
        );
    }

    if (!deliveryNote) {
        return (
            <GlobalLayout>
                <div style={{ padding: 24 }}>
                    <Button icon={<ArrowLeft size={16} />} onClick={() => navigate(urls.deliveryNotes)}>Back</Button>
                    <div style={{ textAlign: 'center', marginTop: 50 }}>
                        <Title level={4}>Delivery Note Not Found</Title>
                    </div>
                </div>
            </GlobalLayout>
        );
    }

    return (
        <GlobalLayout>
            <div style={{ padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Button icon={<ArrowLeft size={16} />} onClick={() => navigate(urls.deliveryNotes)} style={{ marginRight: 16 }} />
                        <Title level={2} style={{ margin: 0 }}>{translate('Delivery Note Details')}</Title>
                    </div>
                    <Space>
                        <Button icon={<Printer size={16} />} onClick={handlePrint}>Print</Button>
                    </Space>
                </div>

                <div ref={componentRef} style={{ padding: 20, background: 'white' }}> {/* Wrapper for print */}
                    <Card bordered={false}>
                        <div style={{ marginBottom: 24 }}>
                            <Title level={3} style={{ textAlign: 'center', marginBottom: 40 }}>DELIVERY NOTE</Title>
                            <Descriptions bordered column={{ xxl: 4, xl: 3, lg: 3, md: 3, sm: 2, xs: 1 }}>
                                <Descriptions.Item label="Delivery #">{deliveryNote.delivery_number}</Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <Tag color={deliveryNote.status === 'delivered' ? 'green' : 'orange'}>
                                        {deliveryNote.status?.toUpperCase()}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Delivery Date">{deliveryNote.delivery_date}</Descriptions.Item>
                                <Descriptions.Item label="Supplier">{deliveryNote.supplier_name}</Descriptions.Item>
                                <Descriptions.Item label="PO Number">{deliveryNote.po_number}</Descriptions.Item>
                                <Descriptions.Item label="Received By">{deliveryNote.received_by_name}</Descriptions.Item>
                                <Descriptions.Item label="Carrier">{deliveryNote.carrier || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Tracking #">{deliveryNote.tracking_number || 'N/A'}</Descriptions.Item>
                            </Descriptions>
                        </div>

                        <div style={{ marginTop: 24 }}>
                            <Title level={4}>Items</Title>
                            <Table 
                                dataSource={deliveryNote.items || []} 
                                columns={columns} 
                                pagination={false} 
                                rowKey="id"
                            />
                        </div>

                        {deliveryNote.delivery_notes && (
                            <div style={{ marginTop: 24 }}>
                                <Title level={5}>Notes</Title>
                                <Typography.Paragraph>{deliveryNote.delivery_notes}</Typography.Paragraph>
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </GlobalLayout>
    );
};

export default DeliveryNoteDetails;
